import React, { useState, useEffect, useCallback } from "react";
import { Container, Button, Modal, Form } from "react-bootstrap";
import { Box } from "@mui/material";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import moment from "moment";
import categoryAPI from "../../../../services/categoryapi";
import DynamicBreadcrumb from "../../../header/breadcrumb";
import CategoryCreateForm from "./CategoryCreateForm";
import CategoryViewModal from "./CategoryViewModal";
import { CatalogHeader, CatalogToolbar, CatalogStatus } from "../../../../components/catalog/CatalogUI";

const AllCategories = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [sortModel, setSortModel] = useState([]);
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [refreshTable, setRefreshTable] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [viewModal, setViewModal] = useState(false);
    const [createModal, setCreateModal] = useState(false);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [reasonText, setReasonText] = useState("");
    const [reasonErrors, setReasonErrors] = useState({});
    const [showReasonModal, setShowReasonModal] = useState(false);
    const [totalCount, setTotalCount] = useState(0);

    const fetchCategories = useCallback(async () => {
        setLoading(true);
        try {
            const { page, pageSize } = paginationModel;
            const response = await categoryAPI.getAllCategories({
                limit: pageSize,
                page,
                sortModel,
                filterModel
            });

            const formatted = response.data.result.map((cat) => ({
                id: cat.categoryID,
                name: cat.categoryName,
                slug: cat.slug,
                status: cat.status,
                createdAt: moment(cat.createdAt).format("YYYY-MM-DD HH:mm"),
                parentName: cat.parent?.categoryName || "Root",
                image: cat.image,
                banner: cat.bannerImage,
            }));

            setRows(formatted);
            setTotalCount(response.data.totalRecords);
        } catch (error) {
            console.error("Failed to load categories", error);
        } finally {
            setLoading(false);
        }
    }, [paginationModel, sortModel, filterModel, refreshTable]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const openViewModal = (id) => {
        setSelectedCategoryId(id);
        setViewModal(true);
    };

    const openEditModal = (id) => {
        setEditingId(id);
        setCreateModal(true);
    };

    const openCreateModal = () => {
        setEditingId(null);
        setCreateModal(true);
    };

    const handleStatusToggle = (id, currentStatus) => {
        const newStatus = currentStatus === 1 ? 0 : 1;

        if (newStatus === 0) {
            setSelectedCategoryId(id);
            setSelectedStatus(newStatus);
            setShowReasonModal(true);
        } else {
            updateCategoryStatus(id, newStatus);
        }
    };

    const updateCategoryStatus = async (id, status, reason = "") => {
        try {
            await categoryAPI.updateCategoryStatus({ id, status, reason });
            setRefreshTable((prev) => !prev);
            setShowReasonModal(false);
            setReasonText("");
        } catch (error) {
            console.error("Failed to update status", error);
        }
    };

    const handleReasonSubmit = () => {
        if (!reasonText.trim()) {
            setReasonErrors({ reasonText: "Reason is required" });
            return;
        }
        setReasonErrors({});
        updateCategoryStatus(selectedCategoryId, selectedStatus, reasonText);
    };

    const columns = [
        { field: "id", headerName: "ID", width: 70 },
        {
            field: "image",
            headerName: "Image",
            width: 100,
            renderCell: (params) => (
                <img
                    src={`${process.env.REACT_APP_IMAGE_URL}/uploads/${params.value}`}
                    alt="category"
                    width={50}
                />
            ),
        },
        {
            field: "banner",
            headerName: "Banner",
            width: 150,
            renderCell: (params) =>
                params.value ? (
                    <img
                        src={`${process.env.REACT_APP_IMAGE_URL}/uploads/${params.value}`}
                        alt="banner"
                        style={{ maxWidth: "100%" }}
                    />
                ) : null,
        },
        { field: "name", headerName: "Category Name", flex: 1 },
        { field: "slug", headerName: "Slug", flex: 1 },
        { field: "parentName", headerName: "Parent Category", flex: 1 },
        { field: "createdAt", headerName: "Created At", flex: 1 },
        {
            field: "status",
            headerName: "Status",
            flex: 1,
            renderCell: (params) => (
                <CatalogStatus active={Number(params.value) === 1} onClick={() => handleStatusToggle(params.id, params.value)} />
            ),
        },
        {
            field: "action",
            headerName: "Actions",
            flex: 1,
            renderCell: (params) => (
                <div className="d-flex gap-2">
                    <Button size="sm" onClick={() => openEditModal(params.id)}>Edit</Button>
                    <Button size="sm" variant="info" onClick={() => openViewModal(params.id)}>View</Button>
                </div>
            ),
        },
    ];

    const exportToCSV = (data, filename = "all_categories.csv") => {
        if (!data || data.length === 0) return;

        const headers = Object.keys(data[0]).join(",");
        const rows = data.map(row =>
            Object.values(row)
                .map(value => `"${value ?? ""}"`) // handle null/undefined
                .join(",")
        );

        const csvContent = [headers, ...rows].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleExportAllData = async () => {
        try {
            const response = await categoryAPI.getAllCategories({
                limit: 10000, // large number to get all
                page: 0,
                sortModel,
                filterModel
            });

            const formatted = response.data.result.map((cat, index) => ({
                SNo: index + 1,
                CategoryName: cat.categoryName,
                Slug: cat.slug,
                ParentCategory: cat.parent?.categoryName || "Root",
                Status: cat.status === 1 ? "Active" : "Inactive",
                CreatedAt: moment(cat.createdAt).format("YYYY-MM-DD HH:mm"),
            }));

            exportToCSV(formatted, "all_categories.csv");
        } catch (error) {
            console.error("Failed to export categories:", error);
        }
    };


    return (
        <Container fluid className="catalog-page">
            <DynamicBreadcrumb pathArray={[{ name: "Categories", url: null }]} />
            <CatalogHeader title="Category Management" description="Manage root categories and subcategories. Search by name, parent or slug." count={totalCount}
                actions={<>
                    <Button variant="secondary" onClick={handleExportAllData}>Export All</Button>
                    <Button variant="primary" onClick={openCreateModal}>Add Category</Button>
                </>} />


            <Box>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    rowCount={totalCount}
                    paginationMode="server"
                    sortingMode="server"
                    filterMode="server"
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    onSortModelChange={setSortModel}
                    onFilterModelChange={(model) => { setFilterModel(model); setPaginationModel((prev) => ({ ...prev, page: 0 })); }}
                    slots={{ toolbar: CatalogToolbar }}
                    getRowHeight={() => "auto"}
                    pageSizeOptions={[10, 25, 50, 100]}
                    disableColumnFilter
                    disableRowSelectionOnClick
                    slotProps={{ toolbar: { showQuickFilter: true } }}
                    sx={{ [`& .${gridClasses.cell}`]: { py: 1 } }}
                />
            </Box>

            {viewModal && selectedCategoryId && (
                <CategoryViewModal
                    show={viewModal}
                    categoryId={selectedCategoryId}
                    onHide={() => setViewModal(false)}
                />
            )}

            {createModal && (
                <CategoryCreateForm
                    show={createModal}
                    editingId={editingId}
                    onHide={() => { setCreateModal(false); setEditingId(null); }}
                    onSuccess={() => setRefreshTable((prev) => !prev)}
                />
            )}

            {/* Reason Modal */}
            <Modal show={showReasonModal} onHide={() => setShowReasonModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Enter Reason for Deactivation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group controlId="reasonTextArea">
                        <Form.Label>Reason</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={reasonText}
                            onChange={(e) => setReasonText(e.target.value)}
                            isInvalid={!!reasonErrors.reasonText}
                        />
                        <Form.Control.Feedback type="invalid">
                            {reasonErrors.reasonText}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowReasonModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleReasonSubmit}>
                        Submit
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AllCategories;
